
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

// Import all UCC Form components
import ElectricalSubcode from 'src/pages/Permits/Construction/TechnicalSections/ElectricalSubcode';
import BuildingSubcode from 'src/pages/Permits/Construction/TechnicalSections/BuildingSubcode';
import PlumbingSubcode from 'src/pages/Permits/Construction/TechnicalSections/PlumbingSubcode'
import FireSubcode from 'src/pages/Permits/Construction/TechnicalSections/FireSubcode'
import MechanicalSubcode from 'src/pages/Permits/Construction/TechnicalSections/MechanicalSubcode';
import ChimneySubcode from 'src/pages/Permits/Construction/TechnicalSections/ChimneySubcode';
import getEnvVars from 'src/config/env';

// Form type mapping
const FormComponents = {
  ELECTRICAL: ElectricalSubcode,
  BUILDING: BuildingSubcode,
  PLUMBING: PlumbingSubcode,
  MECHANICAL: MechanicalSubcode,
  CHIMNEY: ChimneySubcode,
  ['FIRE PROTECTION']: FireSubcode
};

// Public UCC Form component
function PublicUCCForm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const [formType, setFormType] = useState(null);
  const [UCCFormShareID, setUCCFormShareID] = useState(null);
  const [townshipID, setTownshipID] = useState(0);

  const { apiUrl } = getEnvVars();

  const validateToken = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}/construction/validate_ucc_form?uuid=${token}`
      );
      if (response?.status === 200) {
        setFormType(response?.data?.data?.UCCFormName);
        setApplicationId(response?.data?.data?.ApplicationID);
        setUCCFormShareID(response?.data?.data?.UCCFormShareID);
        setTownshipID(response?.data?.data?.TownshipID);
      }
      setLoading(false);
    } catch (e) {
      setError(e.response?.message || 'Invalid or expired link');
      setLoading(false);
    }
  };

  useEffect(() => {
    validateToken();
  }, [token]);

  // Handle form submission
  const handleSubmit = async (data) => {
    try {
      await axios.post(`/api/submit-ucc-form/${token}`, data);
      // Show success message
      alert('Form submitted successfully');
    } catch (e) {
      // Show error message
      alert('Failed to submit form. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={5}>
        <Alert severity="error">
          <Typography variant="h6">Access Error</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Box>
    );
  }

  // Get the correct form component based on form type
  const FormComponent = FormComponents[formType];

  if (!FormComponent) {
    return (
      <Box p={5}>
        <Alert severity="error">
          <Typography>Invalid form type specified</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box p={3} bgcolor="primary.main" color="white">
        <Typography variant="h5">
          {formType?.replace('_', ' ')} UCC Form
        </Typography>
      </Box>

      {/* Form Container */}
      <Box p={5}>
        <FormComponent
          formData={null}
          handleSubmit={handleSubmit}
          readOnly={false} // Add if needed
          token={token} // Pass token if needed for API calls
          applicationId={applicationId}
          isGuest={true}
          UCCFormShareID={UCCFormShareID}
          townshipID={townshipID}
        />
      </Box>

      {/* Footer Info */}
      <Box p={2} mt={3} bgcolor="grey.100">
        <Typography variant="body2" color="textSecondary">
          This form is linked to Construction Permit Application #
          {applicationId}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Please complete and submit the form. You can save it as draft and
          return later using the same link.
        </Typography>
      </Box>
    </Box>
  );
}

export default PublicUCCForm;
